import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/FirebaseAuthContext';
import { useGameSession } from '../context/GameSessionContext';
import { useGame } from '../context/GameContext';
import { GAME_TYPES, GAME_TYPE_DISPLAY_NAMES } from '../config/constants';
import TutorialVideo from './TutorialVideo';

interface PatientGameSelectorProps {
  onGameSelect: (gameType: string) => void;
  currentGame?: string;
}

const PatientGameSelector: React.FC<PatientGameSelectorProps> = ({ onGameSelect, currentGame }) => {
  const { user, logout } = useAuth();
  const { currentSession, isSessionActive, startSession, endSession } = useGameSession();
  const { dispatch } = useGame();
  const [showTutorial, setShowTutorial] = useState(false);

  // Add cleanup effect to end sessions when component unmounts or page is refreshed
  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (isSessionActive) {
        await endSession();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isSessionActive, endSession]);

  const handleGameSelect = async (gameType: string) => {
    try {
      // Always end current session before starting a new one to ensure clean state
      if (isSessionActive) {
        console.log(`Ending current session for ${currentSession?.gameType} before starting ${gameType}`);
        await endSession();
      }
      
      // Start new session for selected game
      console.log(`Starting new session for ${gameType}`);
      await startSession(gameType, `Auto-started session for ${GAME_TYPE_DISPLAY_NAMES[gameType as keyof typeof GAME_TYPE_DISPLAY_NAMES]}`);

      // Initialize game state using authenticated user info
      if (user?.uid) {
        dispatch({ type: 'START_SESSION', payload: { 
          patientId: user.uid, 
          sessionId: `session-${Date.now()}`,
          ageRange: 'Researcher'
        }});
      }

      onGameSelect(gameType);
    } catch (error) {
      console.error('Failed to manage session:', error);
      // Still allow game selection even if session management fails
      if (user?.uid) {
        dispatch({ type: 'START_SESSION', payload: { 
          patientId: user.uid, 
          sessionId: `session-${Date.now()}`,
          ageRange: 'Researcher'
        }});
      }
      onGameSelect(gameType);
    }
  };

  const gameOptions = [
    {
      type: GAME_TYPES.NUMBER_SEQUENCE,
      name: 'Number Sequencing',
      description: 'Remember and recall number sequences',
      icon: '🔢'
    },
    {
      type: GAME_TYPES.MEMORY_PALACE,
      name: 'Memory Palace',
      description: 'Match pairs of images using spatial memory',
      icon: '🏛️'
    },
    {
      type: GAME_TYPES.TONE_MEMORY,
      name: 'Tone Memory',
      description: 'Remember and reproduce musical sequences',
      icon: '🎵'
    },
    {
      type: GAME_TYPES.IMAGE_MATCHING,
      name: 'Image Matching',
      description: 'Find matching pairs in a grid',
      icon: '🎯'
    },
    {
      type: GAME_TYPES.TICKER_TAPE,
      name: 'Ticker Tape Rush',
      description: 'Track moving objects and patterns',
      icon: '📈'
    }
  ];

  return (
    <div className="bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-100 pb-4 overflow-y-scroll animate-gradient-shift" style={{ minHeight: '100vh', height: 'auto' }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
            <div className="text-center sm:text-left">
              <h1 className="text-base sm:text-2xl font-bold text-gray-800 animate-bounce-gentle">
                Welcome, {user?.displayName}! 👋
              </h1>
              <p className="text-xs sm:text-base text-gray-600 animate-fade-in-up">
                Choose a cognitive training game to begin 🧠✨
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
              {isSessionActive && (
                <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs sm:text-sm text-center animate-pulse-gentle border border-green-200">
                  🟢 Session Active: {GAME_TYPE_DISPLAY_NAMES[currentSession?.gameType as keyof typeof GAME_TYPE_DISPLAY_NAMES]}
                </div>
              )}
              <button
                onClick={() => setShowTutorial(true)}
                className="w-full sm:w-auto px-3 py-1.5 bg-indigo-600 text-white hover:bg-indigo-700 rounded-md text-xs sm:text-base flex items-center justify-center space-x-1 transition-colors"
              >
                <span>🎥</span>
                <span>Tutorial</span>
              </button>
              <button
                onClick={logout}
                className="w-full sm:w-auto px-3 py-1.5 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 text-xs sm:text-base"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Game Selection */}
      <div className="max-w-6xl mx-auto px-2 sm:px-4 py-2 sm:py-8 pb-4" style={{ minHeight: 'auto' }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6 mb-4 auto-rows-max" style={{ minHeight: 'auto' }}>
          {gameOptions.map((game) => (
            <div
              key={game.type}
              className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border-2 transform hover:scale-105 hover:-translate-y-2 animate-fade-in ${
                currentGame === game.type 
                  ? 'border-blue-500 ring-4 ring-blue-200 shadow-blue-200/50' 
                  : 'border-gray-200 hover:border-blue-300 hover:shadow-blue-100/30'
              }`}
              onClick={() => handleGameSelect(game.type)}
              style={{ animationDelay: `${gameOptions.indexOf(game) * 150}ms` }}
            >
              <div className="p-3 sm:p-6">
                <div className="text-2xl sm:text-4xl mb-2 sm:mb-4 text-center transform transition-transform duration-300 hover:scale-125 hover:rotate-12">
                  {game.icon}
                </div>
                <h3 className="text-sm sm:text-xl font-semibold text-gray-800 mb-1 sm:mb-2 text-center transition-colors duration-300 hover:text-blue-600">
                  {game.name}
                </h3>
                <p className="text-gray-600 text-center text-[10px] sm:text-sm mb-2 sm:mb-4 transition-colors duration-300 hover:text-gray-700">
                  {game.description}
                </p>
                <div className="text-center">
                  <button
                    className={`w-full sm:w-auto px-3 sm:px-6 py-1.5 sm:py-2 rounded-lg font-medium transition-all duration-300 text-xs sm:text-base transform hover:scale-105 active:scale-95 ${
                      currentGame === game.type
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-blue-300/50'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200 hover:text-blue-800 shadow-md hover:shadow-lg'
                    }`}
                  >
                    {currentGame === game.type ? '🎮 Playing' : '🚀 Start Game'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {showTutorial && <TutorialVideo onClose={() => setShowTutorial(false)} />}
    </div>
  );
};

export default PatientGameSelector;